import type { MetadataConfig } from '@rhybus/metadata-management'

/**
 * Returns a shallow-merged copy of `config` where each form field's `info`
 * property is replaced with the live tooltip text returned by `resolve`.
 *
 * The field's existing `info` value is used as the ToolTipID — it already
 * matches the ToolTipID column values loaded into SharePoint via AppConfig
 * (e.g. 'Title', 'Description', 'Attach File(s)').
 *
 * If no live entry exists for a field's `info` value, the field is left
 * unchanged so the static `info` text continues to show as a fallback.
 *
 * @param config  - The original MetadataConfig object.
 * @param resolve - Resolver from `useTooltipResolver()`.
 *
 * @example
 * const resolve = useTooltipResolver()
 * const config = useMemo(
 *   () => enrichConfigTooltips(announcementsConfig, resolve),
 *   [resolve]
 * )
 */
export function enrichConfigTooltips(
    config: MetadataConfig,
    resolve: (id: string) => string
): MetadataConfig {
    if (!config.form?.fields?.length) return config

    const enrichedFields = config.form.fields.map((field) => {
        // Only process fields that expose an info icon with a value to look up.
        if (!field.isInfo || !field.info) return field
        const liveText = resolve(field.info)
        // If a live entry was found, replace info with the live description.
        // Otherwise keep the field unchanged (static info text continues to show).
        if (!liveText) return field
        return { ...field, info: liveText }
    })

    return {
        ...config,
        form: {
            ...config.form,
            fields: enrichedFields
        }
    }
}
